import { stickersEqual } from "@/model/StickerType";
import type { Board } from "@/model/board";
import { isBacklogBoard, isSolutionBoard } from "@/model/board";
import type { BoardCard } from "@/model/card";
import type { DependencyTeamFilter } from "@/model/search";
import { isDependency } from "@/model/stickyType";
import { matches } from "@/utils/list";

import { useBoardStore } from "../board";
import { resetCardMark, setSearchCardMark } from "../helpers/mark";
import { linkBetween, useLinkStore } from "../link";
import { noAssignee, noReporter } from "../user";
import { useSearchMenuStore } from "./searchMenu";

export function matchesCard(board: Board, card: BoardCard): boolean {
  const store = useSearchMenuStore();

  // If there are no search criteria, don't list any results
  // (but don't grey out any cards, either)
  if (!store.anyFilterCriteria) {
    resetCardMark(card);
    return false;
  }
  const cardMatcher = new CardMatcher(store, card);
  const [teamMatch, dependencyTeamFilter] = cardMatcher.teamMatches;
  const typeMatch = cardMatcher.typeMatches(board);

  const explicitLinkMatch = cardMatcher.explicitLinkMatch(board);
  const linkMatch =
    !useBoardStore().showRiskyLinks || store.selectedRiskyLinks.length === 0 || explicitLinkMatch;
  const propMatch =
    (cardMatcher.flagMatches &&
      teamMatch &&
      cardMatcher.iterationMatches &&
      cardMatcher.assigneeMatches &&
      cardMatcher.reporterMatches &&
      cardMatcher.artMatches &&
      typeMatch &&
      cardMatcher.statusMatches &&
      cardMatcher.upperLevelLinksMatches &&
      cardMatcher.statusDistributionMatches &&
      linkMatch) ||
    explicitLinkMatch;
  const isMatch = cardMatcher.textMatches && propMatch;
  // The active sticky stays visible mid-interaction so a chip-driven attribute
  // change that excludes it from the filter doesn't fade or hide it under the
  // user's cursor. The results panel still respects the truthful isMatch.
  const isActive = useBoardStore().activeCardId === card.data.id;
  setSearchCardMark(card, isMatch || isActive, !propMatch && !isActive, dependencyTeamFilter);
  return isMatch;
}

export class CardMatcher {
  constructor(
    private searchMenuStore: ReturnType<typeof useSearchMenuStore>,
    private card: BoardCard,
  ) {}

  get textMatches(): boolean {
    // Use same matching algo as in the backend piplanningserver/piserver/repos/mongo/sticky.py
    // -> Match ALM id to full query string
    // -> Match any word in the query string to any word in the text
    const searchText = this.searchMenuStore.text.toLowerCase();
    const searchWords = searchText.split(/\s+/).filter(Boolean);
    // filter(Boolean) to handle multiple spaces in the search query, since str.includes('') always returns true

    return (
      searchText.length === 0 ||
      (this.card.data.almId || "").toLowerCase().includes(searchText) ||
      searchWords.some((word) => this.card.data.text.toLowerCase().includes(word))
    );
  }

  get statusMatches(): boolean {
    return matches(this.searchMenuStore.selectedStatusItems, this.card.data.status?.name, "name");
  }

  get flagMatches(): boolean {
    return (
      this.searchMenuStore.flags.length === 0 ||
      this.searchMenuStore.flags.some((flag) => stickersEqual(flag, this.card.data.flagType))
    );
  }

  get iterationMatches(): boolean {
    return matches(this.searchMenuStore.selectedIterations, this.card.data.iterationId, "id");
  }

  get assigneeMatches(): boolean {
    return matches(
      this.searchMenuStore.selectedAssignees,
      this.card.data.assignee?.id || noAssignee("").id,
      "id",
    );
  }

  get reporterMatches(): boolean {
    const reporter = this.card.data.reporter;
    return matches(
      this.searchMenuStore.selectedReporters,
      reporter && reporter.almMapping !== "alm-only" ? reporter.id : noReporter("").id,
      "id",
    );
  }

  get artMatches(): boolean {
    if (!isSolutionBoard(useBoardStore().currentBoard().type) || this.applyDependencySearch) {
      return true;
    }
    return matches(this.searchMenuStore.selectedArts, this.card.data.artId, "id");
  }

  get teamMatches(): [boolean, DependencyTeamFilter | null] {
    if (this.applyDependencySearch && this.searchMenuStore.effectiveTeamIds.size) {
      const dependTeamMatch = this.searchMenuStore.isTeamSelected(this.card.data.dependTeam?.id);
      const precondTeamMatch = this.searchMenuStore.isTeamSelected(this.card.data.precondTeam?.id);

      switch (this.searchMenuStore.selectedDependencyTeamFilter) {
        case "blocked":
          return [precondTeamMatch, "blocked"];
        case "enabling":
          return [dependTeamMatch, "enabling"];
        case "mutual":
          return [dependTeamMatch && precondTeamMatch, "mutual"];
        case "all":
          return [dependTeamMatch || precondTeamMatch, precondTeamMatch ? "blocked" : "enabling"];
      }
    }
    if (useBoardStore().currentBoard().type === "solution") {
      return [true, null];
    }
    return [matches(this.searchMenuStore.selectedTeams, this.card.data.teamId, "id"), null];
  }

  get applyDependencySearch() {
    return this.searchMenuStore.isDependencySearch && isDependency(this.card.data);
  }

  typeMatches(board: Board): boolean {
    const typeMatch = matches(
      this.searchMenuStore.selectedStickyTypes,
      this.card.data.type.id,
      "id",
    );

    const selectedDependencyLink = this.searchMenuStore.selectedDependencyLink;

    if (typeMatch && isDependency(this.card.data) && selectedDependencyLink.size > 0) {
      const anyLinks: boolean = Object.values(board.cards).some((boardCard) =>
        linkBetween(boardCard.data, this.card.data),
      );

      return (
        (selectedDependencyLink.has("Linked") && anyLinks) ||
        (selectedDependencyLink.has("Not Linked") && !anyLinks)
      );
    }

    return typeMatch;
  }

  explicitLinkMatch(board: Board): boolean {
    return this.card.data.linkedStickies.some(
      (link) =>
        useSearchMenuStore().matchesLinkStates(link) &&
        useLinkStore().boardCardByLink(link.stickyId, board),
    );
  }

  get upperLevelLinksMatches(): boolean {
    const selection = this.searchMenuStore.selectedUpperLevelLinks;

    // If "none" selected, always match (no filter applied)
    if (selection.type === "none") {
      return true;
    }

    const linkedFeatures = useLinkStore()
      .getLinkedItems({ type: "sticky", id: this.card.data.id })
      .filter((link) => link.kind === "sticky" && isBacklogBoard(link.type.origin));

    switch (selection.type) {
      case "linked":
        return linkedFeatures.length > 0;

      case "unlinked":
        return linkedFeatures.length === 0;

      case "sticky":
        return linkedFeatures.some((f) => f.id === selection.stickyId);

      default:
        return true;
    }
  }

  get statusDistributionMatches(): boolean {
    const selection = this.searchMenuStore.selectedStatusDistribution;

    if (selection.type === "none") {
      return true;
    }

    // Check if card ID is in the list of sticky IDs from backend
    // OR if this is the parent sticky itself
    if (selection.type === "statusClass") {
      const matchesChildCard = selection.stickyIds.includes(this.card.data.id);
      const matchesParentSticky =
        selection.parentStickyId !== undefined && this.card.data.id === selection.parentStickyId;

      return matchesChildCard || matchesParentSticky;
    }

    return true;
  }
}
